export interface ChangelogEntry {
  version: string
  date: string
  title: string
  highlights: (string | { text: string; underline?: boolean }[])[]
}

// Bump this when a new changelog entry is added. Drives both the app version shown in
// the debug tooltip and the newest changelog entry, keeping them in sync.
const LATEST_VERSION = '1.1'

export const APP_VERSION = `v${LATEST_VERSION}`

// Ordered newest-first.
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: LATEST_VERSION,
    date: 'Nov 2026',
    title: 'Reference Playground & creator history filters',
    highlights: [
      'Create character sheets and scale grids in Reference Playground, with saved runs and a dedicated Reference Images History',
      'Customize character-sheet layouts, appearance, and output shape, and inspect or copy the final Gemini request for either reference generator',
      'Filter Playground, Reference Images, Workflow, and Video history by creator; administrators can search creators by nickname or email',
      'Restore history filters and pages from shared links and browser Back/Forward, including paginated video history'
    ]
  },
  {
    version: '1.0',
    date: 'Oct 2026',
    title: 'Dynamic character onboarding',
    highlights: [
      [
        { text: 'Create or duplicate ' },
        { text: 'dynamic characters', underline: true },
        { text: ' without code changes, draft their setup with AI, test privately, and publish for everyone' }
      ],
      'Customize character and background enrichment templates for each character',
      "Permanently delete archived dynamic characters; their history stays under the 'Deleted characters' filter"
    ]
  },
  {
    version: '0.9',
    date: 'Oct 2026',
    title: 'Media references, models & administration',
    highlights: [
      'Guide videos with image, video, and audio references, or edit and extend existing videos with supported models',
      'Generate images with Nano Banana 2.1, including 1K, 2K, and 4K output and thinking controls',
      'Invite users as members or administrators and manage application access',
      'Track estimated generation costs across Workflow, Playground, and Video'
    ]
  },
  {
    version: '0.8',
    date: 'Sep 2026',
    title: 'Saved image workflows & finishing tools',
    highlights: [
      'Workflow V2 saves image candidates so you can branch generations, build scenes, and finish images with Inpaint, Outpaint, and AI Upscale',
      'Save Playground image runs with prompts, settings, source images, and variants, then continue from any saved run',
      'Expand or reframe images with Outpaint and AI Upscale, with support for 1K, 2K, and 4K output',
      'Create downloadable PNG masks and layout guides with Mask Creator and Outpaint Mask'
    ]
  },
  {
    version: '0.7',
    date: 'Sep 2026',
    title: 'Multi-provider video generation',
    highlights: [
      'Generate videos from still frames with Runway and Gemini Vertex',
      'Choose from Gen-4.5, Veo 3.1 Fast, Seedance 2.5, and Omni video models',
      'Review saved video generations with their inputs and settings, play or download results, and retry failed generations'
    ]
  },
  {
    version: '0.6',
    date: 'Aug 2026',
    title: 'Composition tool',
    highlights: ['Blend a character into a background with the Composition playground tool']
  },
  {
    version: '0.5',
    date: 'Jun 2026',
    title: 'Model picker, folders & reference images',
    highlights: [
      'Choose generation models and fine-tune output settings in each tool',
      'Organize guided workflow generations into folders',
      'Attach reference images to background and inpaint generations',
      'Remove backgrounds for clean transparent cutouts'
    ]
  },
  {
    version: '0.4',
    date: 'Apr 2026',
    title: 'Admin console & image history',
    highlights: [
      'Admin panel to manage per-character configuration',
      'Upload, list and delete character reference images',
      'Browse generation history'
    ]
  },
  {
    version: '0.3',
    date: 'Mar 2026',
    title: 'Multi-character support',
    highlights: [
      [
        { text: 'Generate with ' },
        { text: 'Buzz', underline: true },
        { text: ' (bee) and ' },
        { text: 'Bowie', underline: true },
        { text: ' (cat) alongside Doughboy' }
      ]
    ]
  },
  {
    version: '0.2',
    date: 'Feb 2026',
    title: 'Serverless generation',
    highlights: [
      'Nano Banana serverless character generation via Gemini',
      'Playground for experimenting with individual tools',
      'Prompt enrichment powered by Gemini 2.5 Flash'
    ]
  },
  {
    version: '0.1',
    date: 'Jan 2026',
    title: 'First release',
    highlights: [
      'GPU character generation with ComfyUI on EC2',
      'Track generation status and history',
      'Content moderation for generation requests'
    ]
  }
]
